import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ToastComponent } from './toast';
import { ToastService } from 'svnxii-ng-shared/services';

const LEAVE_DURATION = 220;

@Component({
  selector: 'sh-toast-container',
  standalone: true,
  imports: [CommonModule, ToastComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="pointer-events-none fixed bottom-4 right-4 z-[1000] flex w-full max-w-sm flex-col gap-3 rtl:left-4 rtl:right-auto"
    >
      @for (toast of visibleToasts(); track toast.id) {
        <div
          class="pointer-events-auto overflow-hidden transition-all duration-300 ease-out max-h-24"
          [class.opacity-0]="enteringIds().has(toast.id) || closingIds().has(toast.id)"
          [class.translate-x-6]="isTransitioning(toast.id) && !isRtl()"
          [class.-translate-x-6]="isTransitioning(toast.id) && isRtl()"
          [class.scale-95]="enteringIds().has(toast.id) || closingIds().has(toast.id)"
          [class.opacity-100]="
            !enteringIds().has(toast.id) && !closingIds().has(toast.id)
          "
          [class.translate-x-0]="
            !enteringIds().has(toast.id) && !closingIds().has(toast.id)
          "
          [class.scale-100]="!enteringIds().has(toast.id) && !closingIds().has(toast.id)"
          [class.max-h-0]="closingIds().has(toast.id)"
          [class.mb-0]="closingIds().has(toast.id)"
        >
          <sh-toast
            [toast]="toast"
            (closed)="toastService.dismiss($event)"
            (paused)="toastService.pause($event)"
            (resumed)="toastService.resume($event)"
          />
        </div>
      }
    </div>
  `,
})
export class ToastContainerComponent {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  readonly toastService = inject(ToastService);
  readonly closingIds = signal<Set<number>>(new Set());
  readonly enteringIds = signal<Set<number>>(new Set());
  readonly visibleToasts = signal<any[]>([]);
  readonly isRtl = signal(this.document.documentElement.dir === 'rtl');

  constructor() {
    if (typeof MutationObserver !== 'undefined') {
      const directionObserver = new MutationObserver(() => {
        this.isRtl.set(this.document.documentElement.dir === 'rtl');
      });

      directionObserver.observe(this.document.documentElement, {
        attributes: true,
        attributeFilter: ['dir'],
      });
      this.destroyRef.onDestroy(() => directionObserver.disconnect());
    }

    effect(() => {
      const current = this.toastService.toasts();
      const currentIds = new Set(current.map((t) => t.id));
      const visible = this.visibleToasts();

      visible
        .filter((t) => !currentIds.has(t.id) && !this.closingIds().has(t.id))
        .forEach((t) => this.startClosing(t.id));

      const added = current.filter((t) => !visible.some((v) => v.id === t.id));
      if (added.length) {
        this.visibleToasts.set([...visible, ...added]);
        added.forEach((t) => this.startEntering(t.id));
      }
    });
  }

  isTransitioning(id: number): boolean {
    return this.enteringIds().has(id) || this.closingIds().has(id);
  }

  private startEntering(id: number): void {
    this.enteringIds.update((set) => new Set(set).add(id));
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.enteringIds.update((set) => {
          const next = new Set(set);
          next.delete(id);
          return next;
        });
      });
    });
  }

  private startClosing(id: number): void {
    this.closingIds.update((set) => new Set(set).add(id));
    setTimeout(() => {
      this.visibleToasts.update((list) => list.filter((t) => t.id !== id));
      this.closingIds.update((set) => {
        const next = new Set(set);
        next.delete(id);
        return next;
      });
    }, LEAVE_DURATION);
  }
}
